/**
 * Unified WebSocket Service for Single and Multi-Agent Optimization
 *
 * Endpoint: wss://dev.api.infiano.app/auto-tune/optimize/ws/stream
 *
 * Features:
 * - Single and Multi-Agent optimization modes
 * - Session management with 5-minute timeout
 * - Restore command for reconnection
 * - Stateful RunManager for background work
 */

import { WS_URL } from './config';
import type { OptimizationDatasetNamePayload } from '../utils/optimizationDatasetName';
import type { OptimizationApproach } from '../types/optimizationApproach';
import { buildOptimizationJobApiBody } from '../types/optimizationJob';
import { clearRunSession, readRunSession, startRunSession, updateRunSession } from './optimizationRunSession';
import { getSessionCookie } from '../utils/cookies';

export type OptimizationMode = 'single' | 'multi';

export interface SingleAgentConfig {
  prompt: string;
  dataset_name: OptimizationDatasetNamePayload;
  model_name?: string;
  temperature?: number;
  iterations?: number;
}

export interface MultiAgentConfig {
  dataset_name: OptimizationDatasetNamePayload;
  iterations: number;
  'master-agent': {
    name: string;
    prompt: string;
    model_name: string;
    temperature: number;
    description: string;
  };
  'worker-agents': Array<{
    name: string;
    prompt: string;
    model_name: string;
    temperature: number;
    description: string;
  }>;
  meta_prompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
  };
}

// Новый формат для оптимизации через connector с integration_id
export interface ConnectorMultiAgentConfig {
  integration_id: string;
  /** Display name of the integration config (persisted with optimization run / history). */
  config_label?: string;
  /**
   * Global optimization approach for this run.
   * Must be forwarded to backend as data.approach.
   */
  approach?: OptimizationApproach;
  /**
   * If true, optimization cost is calculated by token; otherwise by another metric.
   * Legacy field — prefer `selection_scenario` in new UI.
   */
  optimize_by_token?: boolean;
  /** How the best candidate is selected after each step. */
  selection_scenario?: 'quality' | 'tokens' | 'cost';
  /** How candidate outputs are evaluated during tuning. */
  evaluation_mode?: import('../types/evaluation').ConfigTuningEvaluationMode;
  /** Mode-specific evaluator settings (embedding model, judge rubric, etc.). */
  evaluator_params?: Record<string, unknown>;
  optimization_policy?: {
    scenario_order: Array<'base' | 'optimize_by_token'>;
    agents: Array<{
      id: string;
      name: string;
      state: 'constant' | 'modifiable';
      scenario?: 'base' | 'optimize_by_token';
    }>;
    meta_prompts: Record<
      string,
      {
        state: 'constant' | 'modifiable';
        scenario?: 'base' | 'optimize_by_token';
      }
    >;
  };
  config: {
    dataset_name: OptimizationDatasetNamePayload;
    iterations: number;
    approach?: OptimizationApproach;
    coordinator: {
      name: string;
      description: string;
      prompt: string;
      model: string;
      temperature?: number;
      models_reasoning?: string;
      use_rag?: boolean;
      use_ltm?: boolean;
      use_diary_read?: boolean;
      use_diary_write?: boolean;
      use_portrait_read?: boolean;
      use_portrait_write?: boolean;
      reminder_prompt?: string;
      id?: string;
      agent_id?: string;
      /** When use_rag: optional allow-list of RAG document ids for this agent. */
      rag_doc_ids?: string[];
    };
    workers: Array<{
      id: string;
      name: string;
      description: string;
      prompt: string;
      mode: 'synchronous' | 'asynchronous';
      model: string;
      temperature?: number;
      models_reasoning?: string;
      use_rag?: boolean;
      rag_doc_ids?: string[];
      use_ltm?: boolean;
      use_diary_read?: boolean;
      use_diary_write?: boolean;
      use_portrait_read?: boolean;
      use_portrait_write?: boolean;
      reminder_prompt?: string;
    }>;
    async_orchestrator?: {
      name?: string;
      model?: string;
      prompt?: string;
      enabled?: boolean;
      description?: string;
      temperature?: number;
      models_reasoning?: string;
      reminder_prompt?: string;
    };
    metaPrompts?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      RAG_TOOL_DESCRIPTION?: string;
      LTM_TOOL_DESCRIPTION?: string;
      [key: string]: string | undefined;
    };
    /** Inject reminder before or after the main prompt (per connector optimization API). */
    reminder_prompt_position?: 'before' | 'after';
  };
}

export interface OptimizationRequest {
  cmd: 'start' | 'restore' | 'stop';
  data: {
    mode?: OptimizationMode;
    config?: SingleAgentConfig | MultiAgentConfig | ConnectorMultiAgentConfig['config'];
    integration_id?: string; // Для нового формата connector оптимизации
    config_label?: string;
    /**
     * Optimization approach for connector multi-agent runs.
     * Example: "BASELINE" or "MULTISTAGE-PACE".
     */
    approach?: OptimizationApproach;
    optimize_by_token?: boolean;
    selection_scenario?: ConnectorMultiAgentConfig['selection_scenario'];
    evaluation_mode?: ConnectorMultiAgentConfig['evaluation_mode'];
    evaluator_params?: ConnectorMultiAgentConfig['evaluator_params'];
    optimization_policy?: ConnectorMultiAgentConfig['optimization_policy'];
    run_id?: string; // Required for restore and stop commands
    last_seq?: number; // Optional for restore command
  };
}

export interface OptimizationResponse {
  type: string;
  data?: any;
  message?: string; // For error messages
}

export interface UnifiedWebSocketCallbacks {
  onProgress?: (progress: number) => void;
  onComplete?: (result: any) => void;
  onError?: (error: any) => void;
  onIntermediateResult?: (result: any) => void;
  onSessionCreated?: (sessionId: string) => void;
}

export class UnifiedWebSocketService {
  private socket: WebSocket | null = null;
  private sessionId: string | null = null;
  private callbacks: UnifiedWebSocketCallbacks = {};
  private intermediateResults: any[] = [];
  private expectedIterations: number = 0;
  private currentIteration: number = 0;
  private isClosing: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10; // Увеличиваем количество попыток
  private reconnectTimeout: number = 300000; // 5 minutes timeout for reconnection
  private connectionTimeout: NodeJS.Timeout | null = null;
  private processedSeqs: Set<number> = new Set<number>(); // track processed seq to avoid duplicates
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isReconnecting: boolean = false;
  private lastMessageTime: number = Date.now();
  private heartbeatInterval: NodeJS.Timeout | null = null;

  private get wsUrl(): string {
    return WS_URL;
  }

  // Run persistence lives in services/optimizationRunSession (one versioned record, AIT-426).

  /**
   * Connect to WebSocket with authentication
   */
  async connect(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        const wsUrl = this.wsUrl;

        // Get authentication token from cookies
        // Check if we have any session cookies
        const sessionCookie = getSessionCookie();

        let finalWsUrl = wsUrl;
        if (sessionCookie) {
          // Add session token as query parameter
          const separator = wsUrl.includes('?') ? '&' : '?';
          finalWsUrl = `${wsUrl}${separator}token=${sessionCookie}`;
        }

        console.log('🔗 Connecting to Unified WebSocket:', finalWsUrl);
        this.socket = new WebSocket(finalWsUrl);

        // Connection timeout
        this.connectionTimeout = setTimeout(() => {
          if (this.socket && this.socket.readyState !== WebSocket.OPEN) {
            this.socket.close();
            reject(new Error('WebSocket connection timeout. Please try again.'));
          }
        }, 10000); // 10 second timeout

        this.socket.addEventListener('open', () => {
          console.log('✅ Unified WebSocket connection opened.');
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
          }
          this.reconnectAttempts = 0;
          this.isReconnecting = false;
          this.lastMessageTime = Date.now();

          // Запускаем heartbeat для отслеживания соединения
          this.startHeartbeat();

          resolve();
        });

        this.socket.addEventListener('message', (event) => {
          try {
            if (this.isClosing) {
              console.log('⚠️ Ignoring message - WebSocket is closing');
              return;
            }

            this.lastMessageTime = Date.now(); // Обновляем время последнего сообщения

            const data: OptimizationResponse = JSON.parse(event.data);
            console.log('📥 Received:', data);

            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        });

        this.socket.addEventListener('close', (event) => {
          console.log(
            `❌ Unified WebSocket closed: code=${event.code}, reason="${event.reason}", clean=${event.wasClean}`
          );
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
          }
          if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
          }
          this.socket = null;

          // Автоматическое переподключение, если есть активная сессия и не было явного закрытия
          if (!this.isClosing && this.sessionId) {
            console.log('🔄 WebSocket closed unexpectedly, attempting to reconnect...');
            this.attemptAutoReconnect();
          }
        });

        this.socket.addEventListener('error', (error) => {
          console.error('⚠️ Unified WebSocket error occurred:', error);
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
          }

          let errorMessage = 'WebSocket connection failed. Please check your internet connection and try again.';

          if (error.toString().includes('401')) {
            errorMessage = 'Authentication failed. Please log in and try again.';
          } else if (error.toString().includes('403')) {
            errorMessage = 'Access denied. Please check your permissions.';
          } else if (error.toString().includes('404')) {
            errorMessage = 'WebSocket endpoint not found. Please contact support.';
          }

          reject(new Error(errorMessage));
        });
      } catch (e) {
        console.error('🚨 Exception while setting up Unified WebSocket:', e);
        reject(e);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: OptimizationResponse): void {
    if (data.message) {
      console.error('❌ WebSocket error:', data.message);
      this.callbacks.onError?.(data.message);
      return;
    }

    switch (data.type) {
      case 'started':
        // Session started - store run_id in memory and localStorage
        if (data.data?.run_id) {
          this.sessionId = data.data.run_id;
          // Persist the run for a page reload; isConnector tells ConnectorPage from MultiAgentPage.
          const isConnector = (this.callbacks as any).isConnector || false;
          startRunSession({
            runId: data.data.run_id,
            mode: data.data.mode ?? null,
            iterations: this.expectedIterations,
            isConnector,
          });
          console.log('✅ Session started with run_id:', data.data.run_id, 'isConnector:', isConnector);
          this.callbacks.onSessionCreated?.(data.data.run_id);
        }
        break;

      case 'update':
        // Process intermediate results
        if (data.data?.data) {
          const updateData = {
            ...data.data.data,
            seq: data.data.seq, // Include seq from the update message
          };

          // Deduplicate by seq so we don't double-apply the same step
          if (typeof updateData.seq === 'number') {
            if (this.processedSeqs.has(updateData.seq)) {
              console.log(`♻️ Skipping duplicate update for seq=${updateData.seq}`);
              break;
            }
            this.processedSeqs.add(updateData.seq);
          }

          // Если сервер прислал seq больше текущего ожидания — поднимаем ожидание
          if (typeof updateData.seq === 'number' && updateData.seq > this.expectedIterations) {
            this.expectedIterations = updateData.seq;
            // Сохраняем обновленное ожидание
            updateRunSession({ iterations: this.expectedIterations });
            // Синхронизируем iterations в zustand store (best-effort)
            import('../store/optimizationStore')
              .then((mod) => {
                try {
                  const { setIterations } = mod.useOptimizationStore.getState();
                  const current = mod.useOptimizationStore.getState().iterations;
                  if (current < this.expectedIterations) {
                    setIterations(this.expectedIterations);
                  }
                } catch (e) {
                  console.warn('⚠️ Failed to sync iterations to store on update:', e);
                }
              })
              .catch(() => {});
          }

          this.currentIteration++;
          const progress = this.calculateProgress();
          this.callbacks.onProgress?.(progress);
          this.intermediateResults.push(updateData);
          console.log(
            `📝 Intermediate result ${this.intermediateResults.length}/${this.expectedIterations}:`,
            updateData
          );
          this.callbacks.onIntermediateResult?.(updateData);
          // Не завершаем сессию автоматически по счетчику — ждём явное сообщение 'complete'
        }
        break;

      case 'restore':
        // Handle restore response with missed iterations
        if (data.data?.steps) {
          console.log(`🔄 Restore response with ${data.data.steps.length} steps`);
          const steps = data.data.steps;

          // Поднимаем ожидаемое кол-во итераций, если видим больший seq
          try {
            const maxSeqInRestore = steps
              .map((s: any) => (typeof s.seq === 'number' ? s.seq : -1))
              .reduce((a: number, b: number) => Math.max(a, b), -1);
            if (maxSeqInRestore >= 0 && maxSeqInRestore > this.expectedIterations) {
              this.expectedIterations = maxSeqInRestore;
              updateRunSession({ iterations: this.expectedIterations });
              console.log(`📋 Adjusted expected iterations from restore: ${this.expectedIterations}`);
              // Синхронизируем iterations в zustand store (best-effort)
              import('../store/optimizationStore')
                .then((mod) => {
                  try {
                    const { setIterations } = mod.useOptimizationStore.getState();
                    const current = mod.useOptimizationStore.getState().iterations;
                    if (current < this.expectedIterations) {
                      setIterations(this.expectedIterations);
                    }
                  } catch (e) {
                    console.warn('⚠️ Failed to sync iterations to store on restore:', e);
                  }
                })
                .catch(() => {});
            }
          } catch (e) {
            console.warn('⚠️ Failed to adjust expected iterations from restore:', e);
          }

          // Process each missed step
          let acceptedCount = 0;
          steps.forEach((step: any) => {
            if (step.data) {
              // Ensure seq is present on restored steps like in 'update' flow
              const stepDataWithSeq = {
                ...step.data,
                seq: step.seq,
              };
              // Deduplicate across multiple restore events
              if (typeof step.seq === 'number') {
                if (this.processedSeqs.has(step.seq)) {
                  console.log(`♻️ Skipping duplicate restore step for seq=${step.seq}`);
                  return;
                }
                this.processedSeqs.add(step.seq);
              }
              this.intermediateResults.push(stepDataWithSeq);
              this.callbacks.onIntermediateResult?.(stepDataWithSeq);
              acceptedCount += 1;
            }
          });

          // Update current iteration
          this.currentIteration += acceptedCount;
          const progress = this.calculateProgress();
          this.callbacks.onProgress?.(progress);

          // If after restore we have all steps (including baseline), finalize optimization
          if (this.expectedIterations > 0) {
            let haveAll = true;
            for (let i = 0; i <= this.expectedIterations; i++) {
              if (!this.processedSeqs.has(i)) {
                haveAll = false;
                break;
              }
            }
            if (haveAll) {
              console.log('✅ All steps restored (including baseline). Finalizing optimization after restore.');
              this.isClosing = true;
              this.processFinalResult({});
              this.clearSession();
            }
          }
        }
        break;

      case 'complete':
        console.log('✅ Optimization completed');
        this.processFinalResult(data.data);
        // Ensure UI reflects completion
        try {
          updateRunSession({ progress: 100 });
          this.callbacks.onProgress?.(100);
          import('../store/optimizationStore')
            .then((mod) => {
              try {
                const { setIsOptimizing } = mod.useOptimizationStore.getState();
                setIsOptimizing(false);
              } catch (e) {
                console.warn('⚠️ Failed to set isOptimizing=false on complete:', e);
              }
            })
            .catch(() => {});
        } catch {}
        // Завершаем сессию и очищаем хранилище, чтобы не было восстановления после перезагрузки
        try {
          this.isClosing = true;
        } finally {
          this.clearSession();
        }
        break;

      case 'error':
        console.error('❌ Optimization error:', data.message);
        this.callbacks.onError?.(data.message || data.data);
        break;

      default:
        console.log('⚠️ Unknown message type:', data.type);
        break;
    }
  }

  /**
   * Calculate progress based on current iteration
   */
  private calculateProgress(): number {
    if (this.expectedIterations <= 0) return 0;

    const totalExpectedResponses = this.expectedIterations + 1; // +1 for first response
    const progressPerResponse = 90 / totalExpectedResponses;
    const currentProgress = this.currentIteration * progressPerResponse;

    const progress = Math.min(currentProgress, 90);

    // Save progress to localStorage
    updateRunSession({ progress: progress });

    return progress;
  }

  /**
   * Process final result and notify callbacks
   */
  private processFinalResult(finalResult: any): void {
    console.log('🎯 Processing final result:', finalResult);

    // Smooth transition to 100%
    this.smoothProgressTo100();

    if (this.intermediateResults.length > 0) {
      // Фильтруем промпты: исключаем seq: 0 (baseline evaluation)
      const prompts = this.intermediateResults
        .filter((result) => result.seq !== 0) // Исключаем seq: 0
        .map((result) => {
          return result.prompt || `Optimized prompt`;
        });

      const limitedPrompts = prompts.slice(0, this.expectedIterations);

      const resultWithPrompts = {
        ...finalResult,
        prompts: limitedPrompts,
        totalIterations: limitedPrompts.length,
      };

      console.log('📋 Final result with prompts array (excluding seq: 0):', resultWithPrompts);
      this.callbacks.onComplete?.(resultWithPrompts);
    } else {
      const resultWithPrompts = {
        ...finalResult,
        prompts: finalResult.prompt ? [finalResult.prompt] : [],
        totalIterations: 1,
      };

      console.log('📋 Final result with single prompt:', resultWithPrompts);
      this.callbacks.onComplete?.(resultWithPrompts);
    }

    // Clear intermediate results
    this.intermediateResults = [];
    this.expectedIterations = 0;
    this.currentIteration = 0;
  }

  /**
   * Smooth progress transition to 100%
   */
  private smoothProgressTo100(): void {
    let currentProgress = 90;
    const targetProgress = 100;
    const duration = 1000;
    const steps = 20;
    const increment = (targetProgress - currentProgress) / steps;
    const interval = duration / steps;

    const progressInterval = setInterval(() => {
      currentProgress += increment;

      if (currentProgress >= targetProgress) {
        currentProgress = targetProgress;
        clearInterval(progressInterval);
      }

      // Save progress to localStorage during smooth transition
      updateRunSession({ progress: currentProgress });

      this.callbacks.onProgress?.(currentProgress);
    }, interval);
  }

  /**
   * Start optimization (single or multi-agent)
   */
  async optimize(
    mode: OptimizationMode,
    config: SingleAgentConfig | MultiAgentConfig | ConnectorMultiAgentConfig,
    callbacks?: UnifiedWebSocketCallbacks & { isConnector?: boolean }
  ): Promise<void> {
    this.callbacks = callbacks || {};
    // Сохраняем флаг isConnector в callbacks для использования в handleMessage
    if (callbacks && 'isConnector' in callbacks) {
      (this.callbacks as any).isConnector = callbacks.isConnector;
    }

    // Reset state
    this.intermediateResults = [];
    this.expectedIterations = 0;
    this.currentIteration = 0;
    this.isClosing = false;
    this.processedSeqs = new Set<number>();

    // Set expected iterations
    if (mode === 'single') {
      const singleConfig = config as SingleAgentConfig;
      this.expectedIterations = singleConfig.iterations || 1;
    } else {
      // Проверяем, это новый формат connector или старый формат
      const connectorConfig = config as ConnectorMultiAgentConfig;
      if (connectorConfig.integration_id && connectorConfig.config) {
        // Новый формат connector
        this.expectedIterations = connectorConfig.config.iterations || 1;
      } else {
        // Старый формат
        const multiConfig = config as MultiAgentConfig;
        this.expectedIterations = multiConfig.iterations || 1;
      }
    }

    console.log(`🔍 Unified WebSocket: Mode: ${mode}, Expected iterations: ${this.expectedIterations}`);

    // Use connectWithRetry for better reliability
    await this.connectWithRetry();

    // Wait for socket to be fully open with retry mechanism
    const maxWaitAttempts = 50; // 5 seconds max wait (50 * 100ms)
    let attempts = 0;

    while (attempts < maxWaitAttempts) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          // Проверяем, это новый формат connector или старый формат
          const connectorConfig = config as ConnectorMultiAgentConfig;
          let request: OptimizationRequest;

          if (connectorConfig.integration_id && connectorConfig.config) {
            request = {
              cmd: 'start',
              data: buildOptimizationJobApiBody(connectorConfig),
            };
          } else {
            // Старый формат: отправляем config как есть
            request = {
              cmd: 'start',
              data: {
                mode,
                config: config as SingleAgentConfig | MultiAgentConfig,
              },
            };
          }

          this.socket.send(JSON.stringify(request));
          console.log('📤 Sent optimization request:', request);
          return; // Successfully sent optimization request
        } catch (sendErr) {
          console.error('❌ Failed to send message:', sendErr);
          throw sendErr;
        }
      }

      // Wait 100ms before checking again
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    // If we get here, socket never opened
    throw new Error('WebSocket connection failed to open. Please try refreshing the page.');
  }

  /**
   * Restore session and continue optimization
   */
  async restore(sessionId: string, callbacks?: UnifiedWebSocketCallbacks & { isConnector?: boolean }): Promise<void> {
    if (!sessionId) {
      throw new Error('Session ID is required for restore');
    }

    this.sessionId = sessionId;
    this.callbacks = callbacks || {};
    // Сохраняем флаг isConnector в callbacks для использования в handleMessage
    if (callbacks && 'isConnector' in callbacks) {
      (this.callbacks as any).isConnector = callbacks.isConnector;
    }
    this.isClosing = false; // Reset closing flag
    // Don't reset intermediateResults and currentIteration here - they will be set from restore response
    this.processedSeqs = new Set<number>(); // reset dedup set on restore

    // Load expected iterations from localStorage
    const storedIterations = readRunSession()?.iterations;
    if (storedIterations) {
      this.expectedIterations = storedIterations;
      console.log(`📋 Restoring with expected iterations: ${this.expectedIterations}`);
    }

    console.log(
      `🔄 Restoring session: ${sessionId}, current state: intermediateResults=${this.intermediateResults.length}, currentIteration=${this.currentIteration}`
    );

    // Use connectWithRetry for better reliability
    await this.connectWithRetry();

    // Wait for socket to be fully open with retry mechanism
    const maxWaitAttempts = 50; // 5 seconds max wait (50 * 100ms)
    let attempts = 0;

    while (attempts < maxWaitAttempts) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          const request: OptimizationRequest = {
            cmd: 'restore',
            data: {
              run_id: sessionId,
            },
          };

          this.socket.send(JSON.stringify(request));
          console.log('📤 Sent restore request:', request);
          return; // Successfully sent restore request
        } catch (sendErr) {
          console.error('❌ Failed to send restore message:', sendErr);
          throw sendErr;
        }
      }

      // Wait 100ms before checking again
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    // If we get here, socket never opened
    throw new Error('WebSocket connection failed to open. Please try refreshing the page.');
  }

  /**
   * Check if user is authenticated before connecting
   */
  private async checkAuthentication(): Promise<{ isAuthenticated: boolean; token?: string }> {
    try {
      const { auth } = await import('./api');
      const response = await auth.checkStatus();
      const isAuthenticated = response.success && response.data?.authenticated === true;

      const token = getSessionCookie() || undefined;

      return { isAuthenticated, token };
    } catch (error) {
      console.error('Failed to check authentication status:', error);
      return { isAuthenticated: false };
    }
  }

  /**
   * Connect with retry logic
   */
  private async connectWithRetry(maxRetries: number = 3): Promise<void> {
    const authResult = await this.checkAuthentication();
    if (!authResult.isAuthenticated) {
      throw new Error('User is not authenticated. Please log in first.');
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.connect();
        return;
      } catch (error) {
        console.warn(`WebSocket connection attempt ${attempt} failed:`, error);

        if (attempt === maxRetries) {
          throw error;
        }

        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(clearSession: boolean = false): void {
    if (this.socket && !this.isClosing) {
      this.isClosing = true;
      console.log('🔌 Disconnecting Unified WebSocket...');
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }

    // Останавливаем heartbeat и таймеры переподключения
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.isReconnecting = false;
    this.intermediateResults = [];
    this.expectedIterations = 0;
    this.currentIteration = 0;
    this.isClosing = false;

    if (clearSession) {
      this.sessionId = null;
      clearRunSession();
    }
  }

  /**
   * Clear stored session
   */
  clearSession(): void {
    this.sessionId = null;
    clearRunSession();
    console.log('🧹 Cleared stored session');
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get stored run_id from localStorage
   */
  getStoredRunId(): string | null {
    return readRunSession()?.runId ?? null;
  }

  /**
   * Get stored run mode from localStorage
   */
  getStoredMode(): OptimizationMode | null {
    return (readRunSession()?.mode ?? null) as OptimizationMode | null;
  }

  /**
   * Get stored isConnector flag from localStorage
   */
  getStoredIsConnector(): boolean {
    return readRunSession()?.isConnector === true;
  }

  /**
   * Check if there's a stored session to restore
   */
  hasStoredSession(): boolean {
    return !!readRunSession();
  }

  /**
   * Get stored progress from localStorage
   */
  getStoredProgress(): number {
    return readRunSession()?.progress ?? 0;
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Stop optimization by sending stop command
   */
  async stop(runId?: string): Promise<void> {
    const targetRunId = runId || this.sessionId || this.getStoredRunId();

    if (!targetRunId) {
      console.warn('⚠️ No run_id available to stop optimization');
      throw new Error('No active optimization session to stop');
    }

    console.log(`🛑 Stopping optimization with run_id: ${targetRunId}`);

    // If socket is not connected, try to connect first
    if (!this.isConnected()) {
      try {
        await this.connectWithRetry(3);
      } catch (error) {
        console.error('❌ Failed to connect for stop command:', error);
        throw new Error('Failed to connect to stop optimization');
      }
    }

    // Wait for socket to be fully open
    const maxWaitAttempts = 50; // 5 seconds max wait
    let attempts = 0;

    while (attempts < maxWaitAttempts) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          const request: OptimizationRequest = {
            cmd: 'stop',
            data: {
              run_id: targetRunId,
            },
          };

          this.socket.send(JSON.stringify(request));
          console.log('📤 Sent stop request:', request);

          // Mark as closing to prevent further processing
          this.isClosing = true;

          return; // Successfully sent stop request
        } catch (sendErr) {
          console.error('❌ Failed to send stop message:', sendErr);
          throw sendErr;
        }
      }

      // Wait 100ms before checking again
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    // If we get here, socket never opened
    throw new Error('WebSocket connection failed to open. Please try refreshing the page.');
  }

  /**
   * Start heartbeat to monitor connection health
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Проверяем соединение каждые 30 секунд
    this.heartbeatInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastMessageTime;

      // Если прошло больше 2 минут без сообщений и есть активная сессия, проверяем соединение
      if (timeSinceLastMessage > 120000 && this.sessionId && !this.isClosing) {
        if (!this.isConnected()) {
          console.log('⚠️ Connection appears to be lost, attempting reconnect...');
          this.attemptAutoReconnect();
        }
      }
    }, 30000); // Проверяем каждые 30 секунд
  }

  /**
   * Attempt automatic reconnection with restore
   */
  private async attemptAutoReconnect(): Promise<void> {
    if (this.isReconnecting) {
      console.log('⚠️ Reconnection already in progress, skipping...');
      return;
    }

    if (!this.sessionId) {
      console.log('⚠️ No session ID available for reconnection');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts (${this.maxReconnectAttempts}) reached. Giving up.`);
      this.callbacks.onError?.('Connection lost and reconnection failed. Please refresh the page and try again.');
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    // Экспоненциальная задержка: 1s, 2s, 4s, 8s, 16s, 32s, 64s, 128s, 256s, 300s (max)
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 300000);
    console.log(
      `🔄 Attempting to reconnect (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`
    );

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(async () => {
      try {
        console.log(`🔄 Reconnecting to session: ${this.sessionId}`);

        // Сохраняем текущие callbacks перед переподключением
        const savedCallbacks = { ...this.callbacks };

        // Переподключаемся и восстанавливаем сессию
        await this.connectWithRetry(3);

        // Восстанавливаем сессию
        if (this.socket && this.socket.readyState === WebSocket.OPEN && this.sessionId) {
          const request: OptimizationRequest = {
            cmd: 'restore',
            data: {
              run_id: this.sessionId,
            },
          };

          this.socket.send(JSON.stringify(request));
          console.log('📤 Sent restore request after reconnection:', request);

          // Восстанавливаем callbacks
          this.callbacks = savedCallbacks;

          console.log('✅ Successfully reconnected and restored session');
          this.isReconnecting = false;
          this.reconnectAttempts = 0; // Сбрасываем счетчик после успешного переподключения
        } else {
          throw new Error('Socket not open after reconnection');
        }
      } catch (error) {
        console.error(`❌ Reconnection attempt ${this.reconnectAttempts} failed:`, error);
        this.isReconnecting = false;

        // Пытаемся снова, если не достигли максимума
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptAutoReconnect();
        } else {
          console.error('❌ All reconnection attempts failed');
          this.callbacks.onError?.('Failed to reconnect. Please refresh the page and try again.');
        }
      }
    }, delay);
  }
}

export const unifiedWebSocketService = new UnifiedWebSocketService();
